<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
  <div id="taskinfo" :class="taskinfoStyle">
    <v-expand-transition>
      <v-card v-show="hasTask">
        <v-list-item two-line class="header" sticky>
          <v-container class="pa-0">
            <v-row align="center">
              <v-col class="py-0 pl-4 pr-0" :md="9" :cols="8">
                <v-form v-model="titleValid">
                  <v-list-item-title class="text-h6 d-flex">
                    <v-icon @click="menuIconClick()">mdi-menu</v-icon>
                    <v-tooltip v-if="task && !changetitleCheck" bottom open-delay="200">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="pl-2 text-truncate">
                          {{ task.name }}
                        </span>
                      </template>
                      <span>{{ task.name }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="task && taskTooLarge && !changetitleCheck" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-if="task" color="red" v-bind="attrs" v-on="on">mdi-alert</v-icon>
                      </template>
                      <span>{{ language.t("Taskinfo.taskTooLarge") }}</span>
                    </v-tooltip>
                    <v-text-field v-model="titleInput" class="pl-2" v-if="task && changetitleCheck" dense
                      :rules="[v => v.length > 0 || language.t('Taskinfo.titleRuleRequired'), v => v.length <= 45 || language.t('Taskinfo.titleRuleMaxlength')]"></v-text-field>
                  </v-list-item-title>

                  <v-list-item-subtitle class="subtitle"><span v-if="task">{{
                    $projections.getByCode(task.projection).name
                      }}</span><span v-if="currentUnit"> - {{ currentUnit }}</span>
                  </v-list-item-subtitle>
                </v-form>
              </v-col>
              <v-col class="py-0 px-4 text-right" :md="3" :cols="4">
                <v-tooltip bottom v-if="taskinfoVisible && !changetitleCheck">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon small :loading="loadTask" @click="changetitleCheck = true">
                      <v-icon>mdi-pencil </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ language.t("Taskinfo.changeTitle") }}</span>
                </v-tooltip>

                <v-tooltip bottom v-if="taskinfoVisible && changetitleCheck">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon small :loading="loadTask" @click="changetitleCheck = false">
                      <v-icon>mdi-pencil-off </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ language.t("Taskinfo.cancelChange") }}</span>
                </v-tooltip>

                <v-tooltip bottom v-if="changetitleCheck">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon small :loading="savingTitle" :disabled="!titleValid"
                      @click="changeTitle">
                      <v-icon color="green">mdi-check </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ language.t("Taskinfo.saveTitle") }}</span>
                </v-tooltip>

                <v-btn icon small @click="toggleTaskinfo()" v-if="!changetitleCheck">
                  <v-icon>mdi-chevron-{{ taskinfoVisible ? "up" : "down" }}</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-list-item>
        <v-list-item dense v-if="task && taskNotLoaded && !oldVersion">
          <v-list-item-icon>
            <v-icon color="red">mdi-alert</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ language.t("Taskinfo.taskLoading") }}</v-list-item-title>
        </v-list-item>

        <v-list-item dense v-if="task && oldVersion" link @click="updateTask()" :disabled="loadTask"
          class="d-flex align-center">
          <v-list-item-icon>
            <v-icon color="red">mdi-alert</v-icon>
          </v-list-item-icon>
          <v-list-item-title class="truncate-text text-left">{{ language.t("Taskinfo.newFiles") }}</v-list-item-title>
        </v-list-item>

        <div v-show="taskinfoVisible" style="max-height:calc(70vh - 52px - 64px)" class="overflow-y-auto">
          <v-list-item dense v-if="task">
            <v-list-item-icon>
              <v-icon>{{ task.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle>{{ task.operationPractice }}</v-list-item-subtitle>
            <v-container class="pa-0">
              <v-row>
                <v-col v-if="hasMotionplanBasic || hasMotionplanSpraypoints" class="py-0 px-4 text-right">

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small @click="toggleMotionplanCreator" 
                        :disabled="task.operationPractice == 'SprayPoints' ||
                        task.operationPractice == 'MotionplanSprayPoints' ||
                        task.operationPractice == 'MotionplanSprayPointsWithBoundary'
                        ">
                        <v-icon color="green">mdi-vector-polyline-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.motionplanNew") }} </span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small @click="toggleMotionplanEditor"
                        :disabled="!task.hasResourceSet('__RESOURCETYPE_MOTIONPLAN') || 
                        (task.operationPractice == 'SprayPoints' ||
                        task.operationPractice == 'MotionplanSprayPoints' ||
                        task.operationPractice == 'MotionplanSprayPointsWithBoundary')">
                        <v-icon color="green">mdi-vector-polyline-edit</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.motionplanEdit") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item>

<!-- 
                      <v-container class="py-0 pr-0" v-if="!changeProjectCheck">
              <v-row align="center">
                <v-col class="pr-0 px-0">
                  <v-list-item-subtitle v-if="!changeProjectCheck && !task.project">{{ language.t("Taskinfo.project")
                  }}:
                    {{ language.t("Taskinfo.noProject") }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="!changeProjectCheck && task.project">{{ language.t("Taskinfo.project")
                  }}:
                    {{ task.project.name }}
                  </v-list-item-subtitle>
                </v-col>
                <v-col class="py-0 px-4 text-right">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="loadTask || loadprojects"
                        @click="changeProject">
                        <v-icon>mdi-pencil </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.editProject") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container> -->


          <!-- SPRAYPOINT ROUTE MODE -->
          <v-list-item dense v-if="task && (task.operationPractice == 'SprayPoints' ||
            task.operationPractice == 'MotionplanSprayPoints' ||
            task.operationPractice == 'MotionplanSprayPointsWithBoundary'
          )">
            <v-list-item-icon>
              <v-icon>mdi-cube-unfolded</v-icon>
            </v-list-item-icon>
            <v-container class="py-0 pr-0">
              <v-row align="center">
                <v-col class="pr-0 px-0">
                  <v-list-item-subtitle>
                    {{ language.t("Taskinfo.contourMode") + ": " }} <span class="font-weight-bold"> {{ language.t("Taskinfo." + task.contour_usage_mode) }}</span>
                  </v-list-item-subtitle>
                </v-col>
                <v-col class="py-0 px-4 text-right">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="loadTask" @click="toggleContourMode" 
                        :disabled="!(task.operationPractice == 'SprayPoints' ||
                        task.operationPractice == 'MotionplanSprayPoints' ||
                        task.operationPractice == 'MotionplanSprayPointsWithBoundary'
                        )">
                        <v-icon  >mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>{{language.t("Taskinfo.changeContourMode") }} </span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item>

          <v-list-item dense v-if="task">
            <v-list-item-icon>
              <v-icon :color="task.statusColor">mdi-square-rounded</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle>
              {{ language.t("Taskinfo.status") }}:
              {{ language.t("Taskinfo." + task.status) }}
            </v-list-item-subtitle>

            <v-container class="pa-0" v-if="task.status === '__TASKSTATUSTYPE_NEW'">
              <v-row>
                <v-col class="py-0 px-4 text-right" v-if="task.operationPractice == 'Survey'">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="loadTask" @click="createTask()">
                        <v-icon color="green">mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Tasklist.convertTask") }}</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="savingStatus || loadTask"
                        @click="changeStatus('__TASKSTATUSTYPE_REMOVED')">
                        <v-icon color="red">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Tasklist.removeSurvey") }}</span>
                  </v-tooltip>
                </v-col>
                <v-col class="py-0 px-4 text-right" v-else>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="savingStatus || loadTask"
                        @click="changeStatus('__TASKSTATUSTYPE_DONE')">
                        <v-icon color="green">mdi-check</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.completeTask") }}</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="savingStatus || loadTask"
                        @click="changeStatus('__TASKSTATUSTYPE_REMOVED')">
                        <v-icon color="red">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.removeTask") }}</span>

                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>

            <v-container class="pa-0" v-else>
              <v-row>
                <v-col class="py-0 px-4 text-right">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="savingStatus || loadTask"
                        @click="changeStatus('__TASKSTATUSTYPE_NEW')">
                        <v-icon color="green">mdi-refresh </v-icon>
                      </v-btn>
                    </template>
                    <span v-if="task.operationPractice == 'Survey'">{{ language.t("Tasklist.restoreSurvey") }}</span>
                    <span v-else>{{ language.t("Taskinfo.recoverTask") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item>

          <v-divider></v-divider>

          <v-list-item dense>
            <v-list-item-icon>
              <v-icon>mdi-comment-text-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle v-if="!changeDescriptionCheck">{{ language.t("Taskinfo.description") }}:
            </v-list-item-subtitle>
            <v-container class="pa-0">
              <v-row>
                <v-col class="py-0 px-4 text-right" v-if="!changeDescriptionCheck">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="loadTask"
                        @click="changeDescriptionCheck = true">
                        <v-icon>mdi-pencil </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.changeDescription") }}</span>
                  </v-tooltip>
                </v-col>
                <v-col class="py-0 px-4 text-right" v-if="changeDescriptionCheck">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="savingDescription"
                        @click="changeDescription">
                        <v-icon color="green">mdi-check </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.saveDescription") }}</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small @click="changeDescriptionCheck = false">
                        <v-icon>mdi-pencil-off</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.cancelChange") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item>
          <v-list-item dense v-if="task && !changeDescriptionCheck">
            <v-list-item-icon>
              <v-icon></v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle>
              {{ task.description }}</v-list-item-subtitle>
          </v-list-item>

          <v-list-item dense v-if="task && changeDescriptionCheck">
            <v-textarea v-model="descriptionInput" name="input-7-4"></v-textarea>
          </v-list-item>

          <v-divider></v-divider>
          <v-list-item dense v-if="task && loadUser">
            <v-list-item-icon>
              <v-icon>mdi-account-edit-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle> {{ language.t("Taskinfo.creatorTask") }}: {{ userMail }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-divider></v-divider>

          <v-list-item dense v-if="task">
            <v-list-item-icon>
              <v-icon>mdi-clock-plus-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle>{{ language.t("Taskinfo.creationDate") }}: {{ task.created }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item dense v-if="task">
            <v-list-item-icon>
              <v-icon>mdi-clock-edit-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle>{{ language.t("Taskinfo.editDate") }}:
              {{ task.edited }}</v-list-item-subtitle>
          </v-list-item>

          <v-divider></v-divider>

          <v-list-item dense v-if="task">
            <v-list-item-icon>
              <v-icon>mdi-folder-open</v-icon>
            </v-list-item-icon>
            <v-container class="py-0 pr-0" v-if="!changeProjectCheck">
              <v-row align="center">
                <v-col class="pr-0 px-0">
                  <v-list-item-subtitle v-if="!changeProjectCheck && !task.project">{{ language.t("Taskinfo.project")
                  }}:
                    {{ language.t("Taskinfo.noProject") }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="!changeProjectCheck && task.project">{{ language.t("Taskinfo.project")
                  }}:
                    {{ task.project.name }}
                  </v-list-item-subtitle>
                </v-col>
                <v-col class="py-0 px-4 text-right">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :loading="loadTask || loadprojects"
                        @click="changeProject">
                        <v-icon>mdi-pencil </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.editProject") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>

            <v-container class="py-0 pr-0" v-if="changeProjectCheck">
              <v-row align="center">
                <v-col class="py-0 px-0" :cols="10">
                  <v-select class="py-1 px-2" v-if="changeProjectCheck" v-model="selectedProject" :items="projectList"
                    dense>
                  </v-select>
                </v-col>
                <v-col class="py-0 px-4 text-right" :cols="2">
                  <v-tooltip bottom v-if="changeProjectCheck">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="px-2" v-if="changeProjectCheck" v-bind="attrs" v-on="on" icon small
                        @click="changeProjectCheck = false">
                        <v-icon>mdi-pencil-off</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.cancelChange") }}</span>
                  </v-tooltip>

                </v-col>
              </v-row>
            </v-container>
          </v-list-item>

          <v-divider></v-divider>
          <v-list-item dense>
            <v-list-item-icon>
              <v-icon> mdi-file-multiple</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle>
              {{ language.t("Taskinfo.fileList") }}
            </v-list-item-subtitle>

            <v-container class="pa-0">
              <v-row>
                <v-col class="py-0 px-4 text-right">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small @click="fileListVisible = !fileListVisible">
                        <v-icon>mdi-chevron-{{
                          fileListVisible ? "up" : "down"
                          }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.viewFileList") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item>
          <div v-if="task && fileListVisible">

            <v-list-item dense v-for="file in task.getFiles().filter((i) => fileVisible(i)).slice().reverse()"
              :key="'' + file.dxffileId + file.dxflayerId">
              <v-list-item-icon>
                <v-icon
                  :color="changesError && file.identifierType == '__IDENTIFIERTYPE_STEERINGLINE' ? 'red' : null">{{
                    file.resourceIcon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-subtitle
                v-if="file.identifierType == '__IDENTIFIERTYPE_MILLINGMODEL' || file.identifierType == '__IDENTIFIERTYPE_DESIGNMODEL' || file.identifierType == '__IDENTIFIERTYPE_CURRENTMODEL'">
                <span>{{
                  language.t(
                    "Taskinfo." + file.identifierType
                  )
                }}:</span>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <a :href="fileDownload(file.dxffileId)" download>{{
                        file.name
                        }}</a>
                    </span>
                  </template>
                  <span>
                    {{ language.t("Taskinfo.downloadFile") }}
                  </span>
                </v-tooltip>
                <br>
                {{ language.t("Taskinfo.features") }}: {{ file.amountFeatures }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-else>
                <v-tooltip bottom
                  v-if="file.identifierType == '__IDENTIFIERTYPE_ORIGINAL' && file.resourceType == '__RESOURCETYPE_DXF'">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" @click="revertVersion(file)"
                      :class="file.identifierType == '__IDENTIFIERTYPE_ORIGINAL' ? 'link' : 'nolink'">
                      {{
                        language.t(
                          "Taskinfo." + file.identifierType
                        )
                      }}:</span>
                  </template>
                  <span>
                    {{ language.t("Taskinfo.revertVersion") }}</span>
                </v-tooltip>
                <span v-else>{{
                  language.t(
                    "Taskinfo." + file.identifierType
                  )
                }}:</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <a :href="fileDownload(file.dxffileId)" download>{{
                        file.name
                        }}</a>
                    </span>
                  </template>
                  <span>
                    {{ language.t("Taskinfo.downloadFile") }}
                  </span>
                </v-tooltip>
              </v-list-item-subtitle>
            </v-list-item>
          </div>

          <div v-if="task && task.isSurvey()">
            <TaskDownloadLink icon="mdi-download" :link="surveyDownload(2)" :text="language.t('Taskinfo.downloadSurvey') + ' 2D (DXF)'"
              :downloadName="task.name + ' 2D'" />
            <TaskDownloadLink icon="mdi-download" :link="surveyDownload(3)" :text="language.t('Taskinfo.downloadSurvey') + ' 3D (DXF)'"
              :downloadName="task.name + ' 3D'" />
          </div>
          <v-divider></v-divider>

          <v-list-item dense v-if="task">
            <v-list-item-icon>
              <v-icon>mdi-clock-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-subtitle>{{
              language.t("Taskinfo.statusHistory")
              }}</v-list-item-subtitle>

            <v-container class="pa-0">
              <v-row>
                <v-col class="py-0 px-4 text-right">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small @click="changeStatusHistoryVisible">
                        <v-icon>mdi-chevron-{{
                          statusHistoryVisible ? "up" : "down"
                          }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ language.t("Taskinfo.viewStatusHistory") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item>

          <v-data-table v-if="statusHistoryVisible" :sort-by="['version']" sort-desc :headers="headers" dense
            :items="task.statuses" :items-per-page="3" :footer-props="{
              itemsPerPageText: language.t('DataTables.itemsPerPageText'),
              itemsPerPageAllText: language.t('DataTables.itemsPerPageAllText'),
              itemsPerPageOptions: [3, 5, -1],
            }
              ">
            <template v-for="header in headers" v-slot:[`header.${header.value}`]>
              <span :key="header.value">{{
                language.t("Taskinfo." + header.text)
                }}</span>
            </template>
            <template v-slot:[`item.description`]="{ item }">
              <v-chip :color="getStatusColor(item.description)" dark small>
                {{
                  language.t("Taskinfo." + item.description)
                }}
              </v-chip>
            </template>
            <template v-slot:[`footer.page-text`]="items">
              {{ items.pageStart }} - {{ items.pageStop }}
              {{ language.t("DataTables.pageText") }} {{ items.itemsLength }}
            </template>
          </v-data-table>
        </div>
        <div v-show="taskinfoVisible">
          <v-card-actions class="card-actions">
            <v-spacer></v-spacer>
            <v-btn @click="closeTask()" text>
              <v-icon>mdi-close</v-icon>{{ language.t("Taskinfo.closeTask") }}
            </v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-expand-transition>
    <TaskCreateSurvey v-if="createTaskFromSurvey" :task="this.task" v-on:close="closeTaskFromSurvey"
      v-on:update="$emit('updateTasks')"></TaskCreateSurvey>

    <TaskError ref="taskError" v-on:close="closeTask" />
  </div>
</template>

<script>
import moment from "moment";
import Task from "../models/Task";
import TaskDownloadLink from "./TaskDownloadLink";
import TaskCreateSurvey from "./robotplotter/TaskCreateSurvey";
import TaskError from "./TaskError";
import * as CONST from "../Constants.js";

export default {
  components: {
    TaskDownloadLink,
    TaskCreateSurvey,
    TaskError
  },
  data() {
    return {
      savingStatus: false,
      savingTitle: false,
      savingDescription: false,
      savingProject: false,
      loadTask: false,
      loadprojects: false,
      loadUser: false,
      changesError: false,
      taskNotLoaded: false,
      oldVersion: false,

      statusString: "",
      changetitleCheck: false,
      changeDescriptionCheck: false,
      changeProjectCheck: false,

      projectList: [],
      selectedProject: null,

      descriptionInput: "",
      titleInput: "",

      titleValid: false,

      statusHistoryVisible: false,
      fileListVisible: true,
      projectString: "",

      userMail: "",

      headers: [
        { text: "date", value: "version" },
        { text: "status", value: "description" },
      ],

      task: null,
      taskinfoVisible: !this.$vuetify.breakpoint.xsOnly,

      taskTooLarge: false,
      taskInfo: {
        project: {},
      },
      createTaskFromSurvey: false,
      currentUnit: "",
    };
  },
  mounted: function () { },
  computed: {
    hasTask: function () {
      return this.task != null;
    },
    taskinfoStyle: function () {
      return {
        floating: !this.$vuetify.breakpoint.xsOnly,
        inline: this.$vuetify.breakpoint.xsOnly,
      };
    },
    customerId() {
      return this.$auth.customerId;
    },
    hasMotionplanBasic() {
      return this.$auth.functionGroups.includes(7)
    },
    hasMotionplanSpraypoints() {
      return this.$auth.functionGroups.includes(9)
    }
  },
  watch: {
    selectedProject(val) {
      if (!this.task) return;
      this.saveProject();
    },
    customerId(val) {
      if (Number.isInteger(val)) {
        this.closeTask()
      }
    },
  },
  methods: {
    setUnit(unit) {
      this.currentUnit = unit;
    },
    checkForTaskUpdates(timeout) {
      setTimeout(() => {
        if (this.task.taskId && !this.oldVersion) {
          this.$axios
            .get(route("api.task", this.task.taskId))
            .then((res) => {
              if (this.task.taskId && !this.oldVersion) {
                console.log("next request in " + (timeout < 30 ? timeout * 2 : 30) + " sec")
                this.checkForTaskUpdates(timeout < 30 ? timeout * 2 : 30)

                if (res.data.edited != this.task.edited) {
                  console.log("TASK VERSION NOT THE SAME")
                  this.$emit("onAddAlert", "opentask", this.language.t("Taskinfo.newFilesAlert"), "info", true);
                  this.oldVersion = true
                }
              }
            })
            .catch((e) => {
              console.error(e);
            });
        }
      }, timeout * 1000);
    },
    toggleMotionplanCreator() {
      this.$emit("toggleMotionplanCreator")
    },
    toggleMotionplanEditor() {
      this.$emit("toggleMotionplanEditor")
    },
    toggleContourMode() {
      // this.$emit("toggleMotionplanEditor")

      this.loadTask = true;
      let data = new FormData();
      if (this.task.contour_usage_mode != "LAST_ONLY") {
        data.set("contour_usage_mode", "LAST_ONLY");
      } else {
        data.set("contour_usage_mode", "ALL");
      }

      this.$axios
        .post(route("api.tasks.contourmode.edit", this.task.taskId), data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          this.loadTask = false;

          //TODO check result
          this.getTask();

        });
    },

    

    getUser() {
      this.$axios.get(route("api.user.customer.user", this.task.creator_accountId)).then((res) => {
        this.userMail = res.data.emailAddress
        this.loadUser = true;
      });
    },
    revertVersion(file) {
      if (file.identifierType != CONST.__IDENTIFIERTYPE_MOTIONPLAN && file.identifierType != CONST.__IDENTIFIERTYPE_ORIGINAL) return;
      this.loadTask = true;
      this.$axios.post(route("api.tasks.revert", [this.task.taskId, file.dxffileId])).then((res) => {
        setTimeout(() => {
          this.checkTask(this.task.taskId);
        }, 2000);
      });
    },
    checkTask(taskId) {
      this.$axios.get(route("api.tasks.dxfactionstatus", taskId)).then((res) => {
        if (res.data.finished != null) {
          this.updateTask();
          this.loadTask = false;
        } else {
          // keep checking for result
          setTimeout(this.checkTask, 10000, taskId);
        }
      });
    },
    updateTask() {
      this.loadTask = true
      const taskId = this.task.taskId;
      this.$emit("closeTask");
      this.$emit("openTask", taskId);
    },
    closeTaskFromSurvey() {
      this.createTaskFromSurvey = false;
    },
    createTask() {
      this.createTaskFromSurvey = true;
    },
    saveProject() {
      this.savingProject = true;
      if (!this.selectedProject) return;
      if (this.selectedProject === this.task.project?.projectId) return;
      if (this.selectedProject === -1 && !this.task.project) return;
      if (this.selectedProject === -1) {
        this.$axios.post(route("api.tasks.projects.remove", this.task.taskId)).then((res) => {
          //TODO check result
          this.getTask();
          this.changeProjectCheck = false;

          this.savingProject = false;
        });
      } else {
        this.$axios.post(route("api.tasks.projects.add", [this.selectedProject, this.task.taskId])).then((res) => {
          //TODO check result
          this.getTask();
          this.changeProjectCheck = false;

          this.savingProject = false;
        });
      }
    },
    changeStatusHistoryVisible() {
      this.statusHistoryVisible = this.statusHistoryVisible ? false : true;
    },
    getSelectedProject() {
      if (this.task.project) {
        this.selectedProject = this.task.project.projectId;
      } else {
        this.selectedProject = -1;
      }
    },
    changeStatus(status) {
      this.savingStatus = true;

      this.$axios.get(route("api.tasks.status", [this.task.taskId, status])).then((res) => {
        this.savingStatus = false;
        this.getTask();
      });
    },
    getProjectList() {
      this.loadprojects = true;
      this.projectList = [{ text: this.language.t("Taskinfo.emptyProject"), value: -1 }, { header: this.language.t("Taskinfo.projectHeader") }];

      this.$axios.get(route("api.projects")).then((res) => {
        for (let i = 0; i < res.data.length; i++) {




          this.projectList.push({
            value: res.data[i].projectId,
            text: res.data[i].name,
          });
        }
        this.loadprojects = false;
      });
    },
    changeTitle() {
      this.savingTitle = true;
      let data = new FormData();
      data.set("title", this.titleInput);

      this.$axios
        .post(route("api.tasks.title.edit", this.task.taskId), data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          //TODO check result
          this.getTask();

          this.changetitleCheck = false;
          this.savingTitle = false;
        });
    },
    changeDescription() {
      this.savingDescription = true;
      let data = new FormData();
      data.set("description", this.descriptionInput);

      this.$axios
        .post(route("api.tasks.description.edit", this.task.taskId), data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          //TODO check result
          this.getTask();

          this.changeDescriptionCheck = false;
          this.savingDescription = false;
        });
    },
    changeProject() {
      this.getSelectedProject();
      this.changeProjectCheck = true;
    },
    getTask() {
      this.loadTask = true;
      if (!this.task) return;
      this.$axios
        .get(route("api.task", this.task.taskId))
        .then((res) => {
          console.log(res.data)
          this.task = new Task(res.data);
          this.descriptionInput = this.task.description ? this.task.description : "";
          this.titleInput = this.task.name;
          this.loadTask = false;
        })
        .catch((e) => {
          console.error(e);
        });
    },
    getStatusColor(status) {
      switch (status) {
        case CONST.__TASKSTATUSTYPE_NEW:
          return "green";
        case CONST.__TASKSTATUSTYPE_DONE:
          return "blue";
        case CONST.__TASKSTATUSTYPE_REMOVED:
          return "red";
        default:
          return "gray";
      }
    },
    openTask(task) {
      console.log(task)
      Object.assign(this.$data, this.$options.data.apply(this));

      if (task.checked === 0) {
        //task is still loading
        this.taskNotLoaded = true
        this.checkForTaskUpdates(2)
      } else {
        this.checkForTaskUpdates(30)
      }

      this.task = new Task(task);
      this.taskTooLarge = false;
      this.descriptionInput = this.task.description ? this.task.description : "";
      this.titleInput = this.task.name;
      this.getProjectList();

      this.task.getFiles().forEach((file) => {
        if (file.amountFeatures > 100000) this.taskTooLarge = true;
      });

      if (this.task.checked == 2) {
        console.log('show task error ');
        this.$axios.get(route("api.tasks.fileprocess", this.task.taskId)).then((res) => {
          if (res.data['file0'] !== undefined) {
            this.$refs.taskError.showError(res.data)
          } else {
            this.changesError = true;
            this.$emit("onAddAlert", "opentask", this.language.t("TaskError.title") + this.language.t("TaskError.contactSupport"), "error", false);
          }
        })

      }

      this.getUser();
    },
    closeTask() {
      var task = this.task;
      if (this.loadTask || this.savingStatus || this.savingDescription || this.savingProject) {
        Object.assign(this.$data, this.$options.data.apply(this));
        this.$emit("closeTask"); // if task info is updating close task without sending information back, could be out of date.
      } else {
        Object.assign(this.$data, this.$options.data.apply(this));
        this.$emit("closeTask" /*task*/); // if nothing is loading data is up to date and can be send back to update tasklist.
      }
    },
    menuIconClick() {
      this.$emit("menuListClick");
    },
    toggleTaskinfo() {
      this.taskinfoVisible = !this.taskinfoVisible;
    },
    fileDownload(id) {
      return route("api.tasks.dxf.download", id);
    },
    surveyDownload(dimensions) {
      return route("api.tasks.exportsurvey", [this.task.taskId, dimensions]);
    },
    fileVisible(file) {
      if (file.identifierType == CONST.__IDENTIFIERTYPE_HEIGHTMAP) return false;
      if (file.resourceType == CONST.__RESOURCETYPE_MOTIONPLAN && file.identifierType == CONST.__IDENTIFIERTYPE_ORIGINAL) return false; // dont show past mp in history for now?
      return true;
    },
    show() {
      this.taskinfoVisible = true;
    },
    hide() {
      this.taskinfoVisible = false;
    }
  },
};
</script>

<style scoped>
#taskinfo {
  z-index: 75;
  margin-left: 5px;
  position: absolute;
  top: 6px;
  left: 2px;
}

.link {
  cursor: pointer;
  text-decoration: underline;
}

.floating {
  width: 450px;
}

.inline {
  width: calc(100% - 7px - 7px);
}

.header {
  padding-left: 9px;
}

.subtitle {
  padding-left: 32px;
}

.truncate-text {
  white-space: pre-wrap;
  /* Allows text to wrap to a new line */
  word-wrap: break-word;
  /* Breaks long words to fit */
  overflow: hidden;
  /* Ensures no overflowing content */
  text-overflow: ellipsis;
  /* Adds ellipsis for truncated text */
}
</style>
